import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Calendar, Filter, ExternalLink } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function Historial() {
  const { token } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, pending, hit, miss

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    try {
      const response = await fetch(`${API_URL}/api/sessions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setSessions(await response.json());
      }
    } catch (error) {
      console.error('Error fetching sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredSessions = sessions.filter(s => {
    if (filter === 'all') return true;
    return s.result === filter;
  });

  // Helper function to get time remaining for pending sessions
  const getTimeRemaining = (targetDateUtc) => {
    if (!targetDateUtc) return null;
    const target = new Date(targetDateUtc);
    const now = new Date();
    const diff = target - now;
    
    if (diff <= 0) return 'Verificando...';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `${days}d ${hours % 24}h`;
    }
    return `${hours}h ${minutes}m`;
  };

  const getResultBadge = (result, targetDateUtc) => {
    if (result === 'pending') {
      const timeLeft = getTimeRemaining(targetDateUtc);
      return (
        <div className="flex flex-col items-start">
          <span className="badge-pending px-3 py-1.5 rounded-full text-xs font-medium">PENDIENTE</span>
          {timeLeft && (
            <span className="text-xs text-white/50 mt-1">
              Faltan {timeLeft}
            </span>
          )}
        </div>
      );
    }
    switch (result) {
      case 'hit':
        return <span className="badge-hit px-3 py-1.5 rounded-full text-xs font-medium">ACIERTO</span>;
      case 'miss':
        return <span className="badge-miss px-3 py-1.5 rounded-full text-xs font-medium">FALLO</span>;
      default:
        return <span className="badge-pending px-3 py-1.5 rounded-full text-xs font-medium">PENDIENTE</span>;
    }
  };

  if (loading) {
    return (
      <div className="p-8 animate-pulse">
        <div className="h-8 bg-white/5 rounded w-48 mb-8"></div>
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="h-16 bg-white/5 rounded-xl"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white">Historial de sesiones</h1>
          <p className="text-white/50 mt-1">{sessions.length} sesiones totales</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-6">
        {[
          { value: 'all', label: 'Todas' },
          { value: 'pending', label: 'Pendientes' },
          { value: 'hit', label: 'Aciertos' },
          { value: 'miss', label: 'Fallos' }
        ].map(f => (
          <button
            key={f.value}
            onClick={() => setFilter(f.value)}
            data-testid={`filter-${f.value}-button`}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === f.value
                ? 'bg-[hsl(198,62%,54%)] text-white'
                : 'bg-white/5 text-white/70 hover:bg-white/10'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Sessions List */}
      <div className="bg-[hsl(232,36%,9%)] border border-[hsl(232,18%,18%)] rounded-xl overflow-hidden">
        {filteredSessions.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-white/50">No hay sesiones con este filtro</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="historial-table">
              <thead>
                <tr className="text-left text-sm text-white/50 border-b border-[hsl(232,18%,18%)] bg-[hsl(232,36%,11%)]">
                  <th className="px-6 py-4 font-medium">Sesión</th>
                  <th className="px-6 py-4 font-medium">Fecha creación</th>
                  <th className="px-6 py-4 font-medium">Fecha objetivo</th>
                  <th className="px-6 py-4 font-medium">Elección</th>
                  <th className="px-6 py-4 font-medium">Confianza</th>
                  <th className="px-6 py-4 font-medium">Resultado</th>
                  <th className="px-6 py-4 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {filteredSessions.map((session) => (
                  <tr 
                    key={session.id} 
                    className="border-b border-[hsl(232,18%,18%)] hover:bg-white/5 transition-colors"
                    data-testid="historial-row"
                  >
                    <td className="px-6 py-4">
                      <span className="font-medium text-white">{session.session_code}</span>
                    </td>
                    <td className="px-6 py-4 text-white/70">
                      {new Date(session.created_at).toLocaleDateString('es-ES', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </td>
                    <td className="px-6 py-4 text-white/70">
                      {new Date(session.target_date_utc).toLocaleDateString('es-ES', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </td>
                    <td className="px-6 py-4 text-white/70">
                      {session.viewer_pick || '-'}
                    </td>
                    <td className="px-6 py-4 text-white/70">
                      {session.confidence ? `${session.confidence}/5` : '-'}
                    </td>
                    <td className="px-6 py-4">
                      {getResultBadge(session.result, session.target_date_utc)}
                    </td>
                    <td className="px-6 py-4">
                      <Link
                        to={`/sesion/${session.id}`}
                        className="text-[hsl(198,62%,54%)] hover:text-[hsl(198,62%,64%)] transition-colors"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
